import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import * as Styled from './styles';
import { GuardedRoute, NotGuardedRoute } from '../';
import { ERouteType, ModalState, RouteItem } from 'shared/types';
import { ROUTES } from './routes';
import { useAppDispatch, useAppSelector } from 'shared/hooks';
import { Sidebar, Loader } from 'shared/components';
import { MenuOutlined } from '@ant-design/icons';
import { openSidebar } from 'services/store/reducers/sidebarReducer';
import { switchLoader } from 'services/store/reducers/loaderReducer';
import { logoutUser } from 'services/store/reducers/authReducer';
import { clearSchools } from 'services/store/reducers/schoolsReducer';
import { Button, Alert } from 'antd';
import { authService } from 'services/api/authService';
import { ESnackbarStyle } from 'shared/types';
import { openNotification } from 'utils/notification-utils';

const MainRoutes = (): JSX.Element => {
  const isAuth = useAppSelector((state): boolean => state.auth.isAuth);
  const isImpersonating = useAppSelector((state): boolean => state.auth.impersonation.isImpersonating);
  const { isModalOpened, modalWindow } = useAppSelector((state): ModalState => state.modal);
  const isLoaderActive = useAppSelector((state): boolean => state.loader.isLoaderActive);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const SuspenseLoading = (): JSX.Element => {
    useEffect((): void => {
      dispatch(switchLoader(true));
    }, []);

    return <div />;
  };

  const handleMenuButtonClick = (): void => {
    dispatch(openSidebar());
  };

  const getRoute = (route: RouteItem): JSX.Element => {
    switch (route.type) {
      case ERouteType.GUARDED:
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              <GuardedRoute
                path={route.path}
                allowedRoles={route.allowedRoles || []}
                element={<route.component />}
              />
            }
          />
        );
      case ERouteType.NOT_GUARDED:
        return (
          <Route
            key={route.path}
            path={route.path}
            element={<NotGuardedRoute path={route.path} element={<route.component />} />}
          />
        );
      default:
        return <Route key={route.path} path={route.path} element={<route.component />} />;
    }
  };

  return (
    <>
      {isLoaderActive && <Loader />}
      <Styled.RootContainer isAuth={isAuth}>
        {isAuth && isImpersonating && (
          <Styled.ImpersonationBannerWrapper>
            <Alert
              message='You are impersonating this admin'
              type='warning'
              showIcon
              action={
                <Button
                  size='small'
                  type='primary'
                  onClick={async (): Promise<void> => {
                    try {
                      const outcome = await authService.returnToSuperAdmin();
                      if (outcome === 'restored') {
                        navigate('/d/admins', { replace: true });
                        openNotification(ESnackbarStyle.SUCCESS, 'Returned to super-admin');
                        return;
                      }
                      // Nothing to restore from (the page was reloaded, this
                      // is a new tab, or an hour has passed): the impersonated
                      // session has been signed out; the admin signs in again.
                      dispatch(clearSchools());
                      dispatch(logoutUser());
                      navigate('/d/login', { replace: true });
                      openNotification(
                        ESnackbarStyle.WARNING,
                        'Your super-admin session could not be restored, so you have been signed out. Please sign in again.'
                      );
                    } catch (error: any) {
                      openNotification(
                        ESnackbarStyle.ERROR,
                        error?.message ?? 'Failed to return to super-admin'
                      );
                    }
                  }}
                >
                  Return to Super Admin
                </Button>
              }
            />
          </Styled.ImpersonationBannerWrapper>
        )}
        {isAuth && (
          <>
            <Styled.MenuButton onClick={handleMenuButtonClick}>
              <MenuOutlined style={{ fontSize: '30px' }} />
            </Styled.MenuButton>
            <Sidebar />
          </>
        )}
        <Suspense fallback={<SuspenseLoading />}>
          <Routes>
            {ROUTES.map((item): JSX.Element => getRoute(item))}
            <Route path='*' element={<Navigate to='/d/not-found' replace />} />
          </Routes>
        </Suspense>
        {isModalOpened && <>{modalWindow}</>}
      </Styled.RootContainer>
    </>
  );
};

export default MainRoutes;
